import React from "react";
import { ChevronLeft, Sparkles, Check, Building2, Target, Users, Compass } from "lucide-react";
import { SectionCard } from "./SectionCard";

/** Step 5 — Final review (Heetch Pro dark) */
export function StepReview({ form, onBack, onEdit, onSubmit, onRestart, saving, onFinalFeedbackChange }) {
  const joinFirst = (arr, n = 3) => {
    if (!Array.isArray(arr) || arr.length === 0) return "";
    const shown = arr.slice(0, n).join(", ");
    return arr.length > n ? `${shown} +${arr.length - n}` : shown;
  };

  return (
    <div className="min-h-screen bg-[var(--bg)] text-[var(--text)]" data-testid="onboarding-step-5">
      <div className="sticky top-0 z-20 bg-[rgba(9,9,11,0.85)] backdrop-blur-md border-b border-[var(--border)] px-5 py-3 flex items-center gap-3">
        <button
          onClick={onBack}
          className="w-10 h-10 rounded-lg border border-[var(--border)] grid place-items-center active:scale-95 shrink-0 text-zinc-300 hover:border-zinc-700 transition-colors"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <div className="flex-1">
          <p className="hp-label" style={{ color: "#86efac" }}>Votre analyse Prosperr</p>
          <p className="font-display font-semibold text-base text-[var(--text)]">Relisez et confirmez</p>
        </div>
        <div className="w-10 h-10 rounded-lg bg-emerald-500/15 border border-emerald-500/30 grid place-items-center">
          <Sparkles className="w-4 h-4 text-emerald-300" strokeWidth={1.8} />
        </div>
      </div>

      <div className="px-5 pt-4 pb-2">
        <div className="rounded-xl px-5 py-4 flex gap-3 items-start border border-emerald-500/30 bg-emerald-500/10">
          <Sparkles className="w-4 h-4 text-emerald-300 shrink-0 mt-0.5" strokeWidth={1.8} />
          <p className="text-sm text-zinc-300 leading-relaxed">
            Prosperr a préparé cette analyse à partir de vos réponses. <strong className="text-[var(--text)]">Confirmez chaque bloc</strong> — ou modifiez ce qui ne correspond pas.
          </p>
        </div>
      </div>

      <div className="px-5 mt-3 space-y-4 pb-4">
        <SectionCard
          title="Votre entreprise"
          icon={Building2}
          onEdit={() => onEdit(1)}
          rows={[
            { label: "Nom",     value: form.company_name },
            { label: "Secteur", value: form.sector },
            { label: "Zone",    value: [form.zone, joinFirst(form.cities, 2)].filter(Boolean).join(" · ") },
          ]}
        />
        <SectionCard
          title="Activité & offre"
          icon={Target}
          onEdit={() => onEdit(2)}
          rows={[
            { label: "Prestations", value: joinFirst(form.prestation_types) },
            { label: "Contextes",   value: joinFirst(form.intervention_contexts) },
            { label: "Certifs",     value: joinFirst(form.certifications) },
            { label: "Fréquence",   value: form.purchase_frequency },
            { label: "Panier",      value: form.basket_range },
            { label: "Cycle",       value: form.cycle_range },
            { label: "Positionnement", value: form.price_positioning },
            { label: "Canaux acq.", value: joinFirst(form.acquisition_channels) },
            { label: "Atouts",      value: joinFirst(form.strengths) },
            { label: "Défi",        value: form.main_challenge },
            { label: "Maturité",    value: form.maturity_level },
          ]}
        />
        <SectionCard
          title="Client idéal"
          icon={Users}
          onEdit={() => onEdit(3)}
          rows={[
            { label: "Structures",   value: joinFirst(form.icp_types) },
            { label: "Tailles",      value: joinFirst(form.icp_sizes) },
            { label: "Secteurs",     value: joinFirst(form.icp_sectors) },
            { label: "Décideurs",    value: joinFirst(form.decider_profiles) },
            { label: "Déclencheurs", value: joinFirst(form.buying_triggers) },
            { label: "Problèmes",    value: joinFirst(form.client_problems) },
            { label: "Budget",       value: form.client_budget },
            { label: "Urgence",      value: form.urgency_level },
            { label: "Critère #1",   value: form.choice_criterion },
          ]}
        />
        <SectionCard
          title="Stratégie & canaux"
          icon={Compass}
          onEdit={() => onEdit(4)}
          rows={[
            { label: "Canal principal", value: form.preferred_channel },
            { label: "Canaux sec.",     value: joinFirst(form.secondary_channels) },
            { label: "Ton",             value: form.communication_tone },
            { label: "Objectif",        value: form.monthly_target ? `${form.monthly_target} clients / mois` : "" },
            { label: "Objections",      value: joinFirst(form.common_objections) },
            { label: "Réponses",        value: joinFirst(form.objection_answers) },
            { label: "Action forte",    value: form.best_action },
            { label: "Action faible",   value: form.hardest_action },
          ]}
        />

        {/* Final feedback */}
        <div className="rounded-xl p-5 border border-emerald-500/40"
          style={{ background: "linear-gradient(135deg, rgba(0,220,130,0.15), rgba(0,220,130,0.04))" }}>
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-4 h-4 text-emerald-300" strokeWidth={1.8} />
            <p className="hp-label" style={{ color: "#86efac" }}>
              Avant de valider
            </p>
          </div>
          <p className="text-sm font-semibold text-[var(--text)] mb-1">
            Qu'est-ce qui manquerait à Prosperr pour être vraiment utile dans votre activité ?
          </p>
          <p className="text-xs text-zinc-500 mb-3">
            Votre retour sert à améliorer Prosperr en continu. Tout est lu.
          </p>
          <textarea
            value={form.final_feedback}
            onChange={(e) => onFinalFeedbackChange(e.target.value)}
            rows={4}
            maxLength={600}
            placeholder="Une fonction qui vous manquerait, un élément mal pris en compte, un besoin spécifique à votre métier…"
            data-testid="final-feedback"
            className="hp-input w-full resize-none"
          />
          <p className="text-[11px] text-zinc-500 text-right mt-1 tabular-nums">{(form.final_feedback || "").length}/600</p>
        </div>
      </div>

      <div className="sticky bottom-0 z-30 bg-[rgba(9,9,11,0.92)] backdrop-blur-md border-t border-[var(--border)] px-5 py-4">
        <div className="max-w-2xl mx-auto space-y-2.5">
          <button
            onClick={onSubmit}
            disabled={saving}
            data-testid="ob-finish"
            className="hp-btn hp-btn-primary w-full h-12"
          >
            <Check className="w-4 h-4" />
            {saving ? "Enregistrement…" : "Valider et démarrer"}
          </button>
          <button
            onClick={onRestart}
            className="hp-btn hp-btn-tertiary w-full h-11"
          >
            Recommencer l'analyse
          </button>
        </div>
      </div>
    </div>
  );
}
